import { createFileRoute } from '@tanstack/react-router'
import { ComparisonPage, comparisonScripts, type ComparisonData } from '@/components/comparison-page'
import { MONITOR_LOCATIONS } from '@/lib/check-locations'

export const Route = createFileRoute('/_site/compare/ohdear')({
  component: () => <ComparisonPage data={data} />,
  head: () => ({
    meta: [
      { title: 'Oh Dear Alternative — Uptrack Uptime Monitoring' },
      { name: 'description', content: "Uptrack vs Oh Dear: Oh Dear starts at $17/mo for 2 sites with 1-min checks. Uptrack checks 50 monitors every 30s for $3/mo." },
      { property: 'og:title', content: 'Oh Dear Alternative — Uptrack' },
      { property: 'og:description', content: "Compare Uptrack and Oh Dear on pricing, check intervals, site health checks, and status pages." },
      { property: 'og:image', content: 'https://uptrack.app/og/compare-ohdear.png' },
      { name: 'twitter:card', content: 'summary_large_image' },
    ],
    links: [
      { rel: 'canonical', href: 'https://uptrack.app/compare/ohdear' },
    ],
    scripts: comparisonScripts('ohdear', "Oh Dear"),
  }),
})

const data: ComparisonData = {
  competitor: "Oh Dear",
  tagline: "Oh Dear checks uptime plus broken links, mixed content, Lighthouse scores, DNS, and more, from $17/mo. Uptrack focuses on 30-second uptime and browser checks, from $3/mo.",
  heroDescription: "These facts come from Oh Dear's public pricing and docs pages as of October 2026. Plans change often, so check their site before you decide.",

  features: [
    { feature: "Starting price", uptrack: "$3/mo (Pro, 50 monitors)", competitor: "$17/mo (Solo, 2 sites)", uptrackWins: true },
    { feature: "Check interval", uptrack: "30 sec", competitor: "1 min", uptrackWins: true },
    { feature: "Down confirmation", uptrack: "2-5 consecutive failed checks; several check locations must confirm", competitor: "Second location confirms; alerts after 2 min by default" },
    { feature: "Check locations", uptrack: MONITOR_LOCATIONS.compare.cell, competitor: "17", uptrackWins: MONITOR_LOCATIONS.compare.wins },
    { feature: "Broken link detection", uptrack: "Sitemap check only (samples up to 50 listed URLs)", competitor: "Full site crawl" },
    { feature: "Mixed content detection", uptrack: "Not available", competitor: "Included" },
    { feature: "Lighthouse reports", uptrack: "Not available", competitor: "Included" },
    { feature: "DNS monitoring", uptrack: "Record-change alerts (A, AAAA, CNAME, MX, TXT, NS, PTR)", competitor: "Included" },
    { feature: "Domain expiry monitoring", uptrack: "RDAP + WHOIS fallback, alerts 30 days ahead (configurable)", competitor: "Included" },
    { feature: "Status pages", uptrack: "5 on Pro, unlimited on Team", competitor: "Included on all plans" },
    { feature: "Users", uptrack: "3 on Pro, 5 on Team, 15 on Business", competitor: "Unlimited on all plans" },
  ],

  painPoints: [
    {
      title: "Price per site",
      description: "Oh Dear Solo is $17/mo for 2 sites, and Solo Plus is $32/mo for 5 sites. Each Oh Dear site covers many check types. Uptrack Pro is $3/mo for 50 monitors, and each monitor runs one check type.",
    },
    {
      title: "Check interval",
      description: "Oh Dear checks every minute. Uptrack checks every 30 seconds on every plan.",
    },
    {
      title: "Browser flows on the entry plan",
      description: "Uptrack Pro includes 5 Playwright browser monitors for $3/mo. Each run follows a flow such as login or checkout and saves a screenshot at each step, so you see which step broke.",
    },
  ],

  differentiators: [
    "Pro starts at $3/mo for 50 monitors at 30-second checks.",
    "Alert confirmation: Uptrack opens an incident only after 2-5 consecutive failed checks, and several check locations must confirm. One DNS timeout or CDN blip does not page you.",
    "Browser checks: Pro includes 5 Playwright monitors that run flows like login or checkout and save a screenshot at each step.",
    "Escalation policies on Team and Business route an unacknowledged alert to the next person after a delay you set.",
    "REST API and an MCP server, so you can manage monitors from scripts or AI assistants such as Claude.",
  ],

  notForYou: [
    "If you need broken link detection, Oh Dear crawls your site and finds dead links. Uptrack doesn't crawl. Its sitemap check only samples up to 50 URLs listed in sitemap.xml.",
    "If you need mixed content detection (HTTP resources on HTTPS pages) or Lighthouse reports, Oh Dear has these built in.",
    "If you work in the Laravel ecosystem, Oh Dear's integrations and community are a strong fit.",
    "If you need application health checks (Oh Dear's health check endpoint monitoring for Laravel/PHP apps).",
    "If you need unlimited users, Oh Dear includes them on every plan.",
  ],
}
